import { Link } from 'react-router-dom'
import { Eye, Clock, Heart, FileText, Video, Music, Megaphone, Star, Play, ExternalLink } from 'lucide-react'
import { type ContentListItem } from '../lib/api'
import MediaTypeBadge from './MediaTypeBadge'
import { useExternalArticle } from '../hooks/useExternalArticle'

interface ContentCardProps {
  item: ContentListItem
  linkPrefix?: string
  onFavorite?: (id: string, favorited: boolean) => void
  favorited?: boolean
  showFavorite?: boolean
  /** rich: 封面在上的竖版卡片（默认）；compact: 横版紧凑卡片 */
  variant?: 'rich' | 'compact'
}

const mediaPlaceholder: Record<string, { bg: string; text: string; Icon: typeof FileText; label: string }> = {
  text: { bg: 'from-blue-50 to-blue-100', text: 'text-blue-400', Icon: FileText, label: '图文' },
  video: { bg: 'from-purple-50 to-purple-100', text: 'text-purple-400', Icon: Play, label: '视频' },
  audio: { bg: 'from-orange-50 to-orange-100', text: 'text-orange-400', Icon: Music, label: '音频' },
}

/** 外链条目角标（让用户对「点了会跳走」有预期） */
const ExternalBadge = ({ className = '' }: { className?: string }) => (
  <span
    className={
      'inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100 ' +
      className
    }
  >
    <ExternalLink className="w-2.5 h-2.5" />
    外链
  </span>
)

const ContentCard = ({ item, linkPrefix = '/user/knowledge', onFavorite, favorited = false, showFavorite = false, variant = 'rich' }: ContentCardProps) => {
  const isNotice = item.parent_category_name === '区县公告' || item.category_name === '区县公告'
  const dateStr = (item.published_at || item.created_at || '').slice(0, 10)
  const ph = mediaPlaceholder[item.media_type] || mediaPlaceholder.text

  /* 外链文章：配了 link_url 的条目点击「直接跳转」，不进详情页。
     跳转规则（公众号直跳 / 其他在微信内引导复制）见 lib/externalLink.ts。 */
  const externalUrl = (item.link_url || '').trim()
  const isExternal = !!externalUrl
  const { openLink, dialog } = useExternalArticle()
  const openExternal = () => openLink(externalUrl)

  /**
   * 根容器：普通条目用 <Link>（保留新窗口打开、右键菜单等原生行为）；
   * 外链条目用 <button>（点击即跳转，不做路由导航）。
   *
   * ⚠️ 这里必须是**普通函数调用** `renderRoot(...)`，不能在 render 里写
   *   `const Root = ({...}) => ...` 再去 `<Root>` 使用：那样每次渲染都会产生
   *   新的组件类型，React 会把整棵子树卸载重建 —— 列表滚动时卡片封面会
   *   重新加载、过渡动画重播。函数调用返回的 JSX 直接内联进父树，没这个问题。
   */
  const renderRoot = (className: string, children: React.ReactNode) =>
    isExternal ? (
      <button type="button" onClick={openExternal} className={`w-full text-left ${className}`}>
        {children}
      </button>
    ) : (
      <Link to={`${linkPrefix}/${item.id}`} className={className}>
        {children}
      </Link>
    )

  const Meta = (
    <div className="flex items-center gap-2.5 text-[11px] text-gray-400">
      <span className="inline-flex items-center gap-0.5"><Eye className="w-3 h-3" />{item.view_count || 0}</span>
      {dateStr && <span className="inline-flex items-center gap-0.5"><Clock className="w-3 h-3" />{dateStr}</span>}
    </div>
  )
  const FavBtn = showFavorite ? (
    <button
      onClick={e => { e.preventDefault(); e.stopPropagation(); onFavorite?.(item.id, !favorited) }}
      className={`p-1.5 rounded-full transition-colors ${favorited ? 'text-red-500 bg-red-50' : 'text-gray-300 hover:text-red-400 hover:bg-red-50'}`}
      aria-label={favorited ? '取消收藏' : '收藏'}
    >
      <Heart className={`w-3.5 h-3.5 ${favorited ? 'fill-current' : ''}`} />
    </button>
  ) : null

  if (variant === 'compact') {
    const Icon = item.media_type === 'video' ? Video : item.media_type === 'audio' ? Music : FileText
    return (
      <>
        {renderRoot(
          'block bg-white rounded-2xl shadow-sm hover:shadow-md transition-all overflow-hidden',
          (
            <div className="flex gap-3 p-3">
              {item.cover_url ? (
                <img src={item.cover_url} alt={item.title} className="w-28 h-20 object-cover rounded-xl flex-shrink-0" loading="lazy" />
              ) : (
                <div className={`w-28 h-20 rounded-xl flex-shrink-0 flex items-center justify-center bg-gradient-to-br ${ph.bg}`}>
                  <Icon className={`w-6 h-6 ${ph.text}`} />
                </div>
              )}
              <div className="flex-1 min-w-0 flex flex-col">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <MediaTypeBadge type={item.media_type} />
                  {isExternal && <ExternalBadge />}
                  {item.is_recommended === 1 && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-100">
                      <Star className="w-2.5 h-2.5" />推荐
                    </span>
                  )}
                  {isNotice && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-100">
                      <Megaphone className="w-2.5 h-2.5" />公告
                    </span>
                  )}
                </div>
                <h3 className="font-medium text-gray-800 mt-1.5 line-clamp-2 text-sm leading-snug">{item.title}</h3>
                {item.summary && <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{item.summary}</p>}
                <div className="flex items-center justify-between text-[10px] text-gray-400 mt-auto pt-1">
                  {Meta}
                  {showFavorite && (
                    <button
                      onClick={e => { e.preventDefault(); e.stopPropagation(); onFavorite?.(item.id, !favorited) }}
                      className={`p-1 rounded-full transition-colors ${favorited ? 'text-red-500' : 'text-gray-400 hover:text-red-400'}`}
                    >
                      <Heart className={`w-3.5 h-3.5 ${favorited ? 'fill-current' : ''}`} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ),
        )}
        {dialog}
      </>
    )
  }

  return (
    <>
      {renderRoot(
        'group block bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:border-green-200 hover:-translate-y-0.5 transition-all duration-300 overflow-hidden',
        (
          <>
            {/* 封面区 */}
            <div className="relative aspect-video overflow-hidden bg-gray-50">
              {item.cover_url ? (
                <img
                  src={item.cover_url}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              ) : (
                <div className={`w-full h-full bg-gradient-to-br ${ph.bg} flex items-center justify-center`}>
                  <ph.Icon className={`w-10 h-10 ${ph.text} opacity-70 group-hover:scale-110 transition-transform duration-300`} />
                </div>
              )}
              {/* 视频播放角标 */}
              {item.media_type === 'video' && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-11 h-11 rounded-full bg-black/35 backdrop-blur-sm flex items-center justify-center group-hover:bg-black/50 group-hover:scale-110 transition-all">
                    <Play className="w-5 h-5 text-white fill-white ml-0.5" />
                  </div>
                </div>
              )}
              {/* 左上角媒体类型角标 */}
              <div className="absolute top-2.5 left-2.5">
                <MediaTypeBadge type={item.media_type} size="sm" />
              </div>
              {/* 右上角外链/推荐/公告角标 */}
              <div className="absolute top-2.5 right-2.5 flex gap-1.5">
                {isExternal && <ExternalBadge className="bg-white/90 backdrop-blur border-0 shadow-sm" />}
                {item.is_recommended === 1 && (
                  <span className="inline-flex items-center gap-0.5 text-[10px] px-2 py-0.5 rounded-full bg-white/90 backdrop-blur text-red-600 shadow-sm">
                    <Star className="w-2.5 h-2.5 fill-current" />推荐
                  </span>
                )}
                {isNotice && (
                  <span className="inline-flex items-center gap-0.5 text-[10px] px-2 py-0.5 rounded-full bg-white/90 backdrop-blur text-amber-600 shadow-sm">
                    <Megaphone className="w-2.5 h-2.5" />公告
                  </span>
                )}
              </div>
            </div>
            {/* 内容区 */}
            <div className="p-3 flex flex-col gap-1">
              <h3 className="font-semibold text-gray-800 text-sm leading-snug line-clamp-2 group-hover:text-green-700 transition-colors">
                {item.title}
              </h3>
              {item.summary && <p className="text-xs text-gray-500 line-clamp-1">{item.summary}</p>}
              <div className="flex items-center justify-between pt-1.5 mt-auto">
                {Meta}
                {showFavorite && FavBtn}
              </div>
            </div>
          </>
        ),
      )}
      {dialog}
    </>
  )
}

export default ContentCard
